import * as React from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { formatNumber, formatDuration } from '@/lib/utils'

interface DPSTimelineProps {
  data: {
    timestamp: number
    dps: number
  }[]
  title?: string
  description?: string
  className?: string
}

export const DPSTimeline: React.FC<DPSTimelineProps> = ({
  data,
  title = 'DPS Timeline',
  description = 'Damage per second over the course of the encounter',
  className,
}) => {
  const chartData = data.map((point) => ({
    time: formatDuration(point.timestamp),
    timestamp: point.timestamp,
    dps: point.dps,
  }))

  const maxDPS = Math.max(...data.map(d => d.dps))
  const avgDPS = data.reduce((sum, d) => sum + d.dps, 0) / data.length

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-md">
          <p className="font-medium">{`Time: ${label}`}</p>
          <p className="text-primary">
            {`DPS: ${formatNumber(payload[0].value)}`}
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <div className="text-right space-y-1">
            <div className="text-sm">
              <span className="text-muted-foreground">Peak: </span>
              <span className="font-medium text-green-600">
                {formatNumber(maxDPS)}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">Avg: </span>
              <span className="font-medium">
                {formatNumber(Math.round(avgDPS))}
              </span>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="time" 
                className="text-xs"
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tickFormatter={formatNumber}
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="dps" 
                stroke="hsl(var(--primary))" 
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: 'hsl(var(--primary))', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}